import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Settings2,
  ChevronDown,
  Square,
  RectangleVertical,
  Smartphone,
  Monitor,
  Sparkles,
  Palette,
} from "lucide-react";
import type { SelectedStyleConfig } from "./types";
import { dictionary, Language } from "@/utils/dictionary";
import { Textarea } from "@/components/ui/textarea";
import CoinFluxi from "@/assets/icons/coin-fluxi.svg";
import { cn } from "@/lib/utils";

const INSTRUCTION_PLACEHOLDER_KEYS = [
  "instruction-placeholder-1",
  "instruction-placeholder-2",
  "instruction-placeholder-3",
  "instruction-placeholder-4",
];

const FORMAT_OPTIONS: {
  value: SelectedStyleConfig["format"];
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: "1:1", label: "1:1", icon: <Square className="h-3.5 w-3.5" strokeWidth={1.5} /> },
  { value: "4:5", label: "4:5", icon: <RectangleVertical className="h-3.5 w-3.5" strokeWidth={1.5} /> },
  { value: "9:16", label: "9:16", icon: <Smartphone className="h-3.5 w-3.5" strokeWidth={1.5} /> },
  { value: "16:9", label: "16:9", icon: <Monitor className="h-3.5 w-3.5" strokeWidth={1.5} /> },
];

const COLOR_PRESETS = [
  { primary: "#4368E5", bg: "#FFFFFF", name: "Fluxi" },
  { primary: "#FF6B6B", bg: "#FFF5F5", name: "Coral" },
  { primary: "#00C853", bg: "#F0FFF4", name: "Verde" },
  { primary: "#FFB800", bg: "#FFFBEB", name: "Oro" },
  { primary: "#1A1A1A", bg: "#FFFFFF", name: "Mono" },
  { primary: "#4368E5", bg: "#FAF5FF", name: "Violeta" },
];

interface AdStudioSidebarProps {
  selectedStyles: SelectedStyleConfig[];
  expandedConfigs: Set<string>;
  onRemoveStyle: (styleId: string) => void;
  onToggleConfig: (styleId: string) => void;
  onUpdateConfig: (styleId: string, updates: Partial<SelectedStyleConfig>) => void;
  maxSelection?: number;
  language: Language;
  canGenerate?: boolean;
  onGenerate?: () => void;
}

export function AdStudioSidebar({
  selectedStyles,
  expandedConfigs,
  onRemoveStyle,
  onToggleConfig,
  onUpdateConfig,
  maxSelection = 3,
  language,
  canGenerate = false,
  onGenerate,
}: AdStudioSidebarProps) {
  const totalCredits = selectedStyles.length * 13;

  return (
    <div className="w-[40%] border-l border-border flex flex-col flex-shrink-0">
      <div className="p-5 flex-1 overflow-y-auto">
        {/* Title */}
        <div className="mb-4">
          <h3 className="text-sm font-bold text-foreground">
            {dictionary["your-images"]?.[language as Language] || "Tus Imágenes"}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {dictionary["configure-each-image-before-generating"]?.[language as Language] ||
              "Configura cada imagen antes de generar"}
          </p>
        </div>

        {selectedStyles.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-border/40 p-6 text-center">
            <p className="text-sm text-muted-foreground/60">
              {dictionary["select-styles-from-the-gallery"]?.[language as Language] ||
                "Selecciona estilos de la galería"}
            </p>
          </div>
        ) : (
          <div>
            <div className="space-y-2">
            {selectedStyles.map((config, configIndex) => {
              const isExpanded = expandedConfigs.has(config.style.id);
              const styleName =
                config.style.name ||
                `${dictionary["style"]?.[language as Language] || "Estilo"} ${config.style.id}`;
              const hasCustomization =
                config.format !== "1:1" ||
                config.colors !== "auto" ||
                (config.instructions?.trim() ?? "") !== "";
              const primaryColor = config.primaryColor ?? "#4368E5";
              const backgroundColor = config.backgroundColor ?? "#FFFFFF";

              return (
                <div
                  key={config.style.id}
                  className="rounded-xl border border-border/60 bg-card overflow-hidden"
                >
                  {/* Style Card Header */}
                  <div className="flex items-center gap-3 p-2.5">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted/20 flex-shrink-0">
                      <img
                        src={config.style.image_url}
                        alt={styleName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground truncate flex-1 min-w-0">
                      {styleName}
                    </span>

                    <button
                      type="button"
                      onClick={() => onRemoveStyle(config.style.id)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Configuration Accordion */}
                  <div className="px-2.5 pb-2">
                    <div className="mt-1.5">
                      <button
                        type="button"
                        onClick={() => onToggleConfig(config.style.id)}
                        className={cn(
                          "w-full flex items-center gap-1.5 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-colors",
                          isExpanded
                            ? "text-primary bg-primary/5"
                            : hasCustomization
                              ? "text-primary/80 hover:bg-primary/5"
                              : "text-muted-foreground hover:bg-muted/50"
                        )}
                      >
                        <Settings2 className="h-3 w-3" />
                        <span>{dictionary["configuration"]?.[language as Language] || "Configuración"}</span>
                        {hasCustomization && !isExpanded && (
                          <span className="ml-auto mr-1 w-1.5 h-1.5 rounded-full bg-primary" />
                        )}
                        <ChevronDown
                          className={cn(
                            "h-3 w-3 ml-auto transition-transform duration-200",
                            isExpanded && "rotate-180"
                          )}
                        />
                      </button>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="pt-2.5 pb-1 px-1 space-y-3">
                              {/* Format */}
                              <div>
                                <p className="text-[10px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                                  {dictionary["format"]?.[language as Language] || "Formato"}
                                </p>
                                <div className="grid grid-cols-4 gap-1.5">
                                  {FORMAT_OPTIONS.map((opt) => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => onUpdateConfig(config.style.id, { format: opt.value })}
                                      className={cn(
                                        "flex flex-col items-center gap-1 py-2 rounded-lg border transition-all duration-150",
                                        config.format === opt.value
                                          ? "border-primary bg-primary/5 text-primary"
                                          : "border-border/60 text-muted-foreground hover:border-foreground/20"
                                      )}
                                    >
                                      {opt.icon}
                                      <span className="text-[10px] font-semibold">{opt.label}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Colors */}
                              <div>
                                <p className="text-[10px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                                  {dictionary["colors"]?.[language as Language] || "Colores"}
                                </p>
                                <div className="grid grid-cols-2 gap-1.5 mb-2">
                                  <button
                                    type="button"
                                    onClick={() => onUpdateConfig(config.style.id, { colors: "auto" })}
                                    className={cn(
                                      "flex items-center gap-1.5 p-2 rounded-lg border transition-all duration-150",
                                      config.colors === "auto"
                                        ? "border-primary bg-primary/5"
                                        : "border-border/60 hover:border-foreground/20"
                                    )}
                                  >
                                    <Sparkles
                                      className={cn(
                                        "h-3.5 w-3.5",
                                        config.colors === "auto" ? "text-primary" : "text-muted-foreground"
                                      )}
                                    />
                                    <span
                                      className={cn(
                                        "text-[11px] font-semibold",
                                        config.colors === "auto" ? "text-primary" : "text-foreground"
                                      )}
                                    >
                                      {dictionary["auto"]?.[language as Language] || "Auto"}
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      onUpdateConfig(config.style.id, {
                                        colors: "custom",
                                        primaryColor: primaryColor,
                                        backgroundColor: backgroundColor,
                                      })
                                    }
                                    className={cn(
                                      "flex items-center gap-1.5 p-2 rounded-lg border transition-all duration-150",
                                      config.colors === "custom"
                                        ? "border-primary bg-primary/5"
                                        : "border-border/60 hover:border-foreground/20"
                                    )}
                                  >
                                    <Palette
                                      className={cn(
                                        "h-3.5 w-3.5",
                                        config.colors === "custom" ? "text-primary" : "text-muted-foreground"
                                      )}
                                    />
                                    <span
                                      className={cn(
                                        "text-[11px] font-semibold",
                                        config.colors === "custom" ? "text-primary" : "text-foreground"
                                      )}
                                    >
                                      {dictionary["custom"]?.[language as Language] || "Custom"}
                                    </span>
                                  </button>
                                </div>

                                <AnimatePresence>
                                  {config.colors === "custom" && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: "auto" }}
                                      exit={{ opacity: 0, height: 0 }}
                                      transition={{ duration: 0.15 }}
                                      className="overflow-hidden"
                                    >
                                      <div className="flex gap-1.5 flex-wrap mb-2">
                                        {COLOR_PRESETS.map((preset) => (
                                          <button
                                            key={preset.name}
                                            type="button"
                                            onClick={() =>
                                              onUpdateConfig(config.style.id, {
                                                primaryColor: preset.primary,
                                                backgroundColor: preset.bg,
                                              })
                                            }
                                            className={cn(
                                              "flex flex-col items-center gap-0.5 p-1 rounded-md transition-all duration-150",
                                              primaryColor === preset.primary &&
                                                backgroundColor === preset.bg
                                                ? "ring-1.5 ring-primary bg-primary/5"
                                                : "hover:bg-muted/50"
                                            )}
                                          >
                                            <div className="w-5 h-5 rounded overflow-hidden border border-border/50">
                                              <div className="h-1/2" style={{ backgroundColor: preset.primary }} />
                                              <div className="h-1/2" style={{ backgroundColor: preset.bg }} />
                                            </div>
                                            <span className="text-[9px] font-medium text-muted-foreground">
                                              {dictionary[preset.name.toLowerCase() as keyof typeof dictionary]?.[language as Language] ?? preset.name}
                                            </span>
                                          </button>
                                        ))}
                                      </div>

                                      <div className="grid grid-cols-2 gap-1.5">
                                        <div className="relative">
                                          <input
                                            type="color"
                                            value={primaryColor}
                                            onChange={(e) =>
                                              onUpdateConfig(config.style.id, {
                                                primaryColor: e.target.value,
                                              })
                                            }
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                          />
                                          <div className="p-2 rounded-lg border border-border hover:border-foreground/20 transition-colors cursor-pointer">
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-5 h-5 rounded"
                                                style={{ backgroundColor: primaryColor }}
                                              />
                                              <div>
                                                <p className="text-[9px] text-muted-foreground">
                                                  {dictionary["primary"]?.[language as Language] || "Primario"}
                                                </p>
                                                <p className="text-[10px] font-semibold text-foreground font-mono">
                                                  {primaryColor.toUpperCase()}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="relative">
                                          <input
                                            type="color"
                                            value={backgroundColor}
                                            onChange={(e) =>
                                              onUpdateConfig(config.style.id, {
                                                backgroundColor: e.target.value,
                                              })
                                            }
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                          />
                                          <div className="p-2 rounded-lg border border-border hover:border-foreground/20 transition-colors cursor-pointer">
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-5 h-5 rounded border border-border/30"
                                                style={{ backgroundColor: backgroundColor }}
                                              />
                                              <div>
                                                <p className="text-[9px] text-muted-foreground">
                                                  {dictionary["background"]?.[language as Language] || "Fondo"}
                                                </p>
                                                <p className="text-[10px] font-semibold text-foreground font-mono">
                                                  {backgroundColor.toUpperCase()}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>

                              {/* Instructions */}
                              <div>
                                <p className="text-[10px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                                  {dictionary["instructions"]?.[language as Language] || "Instrucciones"}{" "}
                                  <span className="font-normal text-muted-foreground/50">({dictionary["optional"]?.[language as Language] || "OPCIONAL"})</span>
                                </p>
                                <Textarea
                                  value={config.instructions}
                                  onChange={(e) =>
                                    onUpdateConfig(config.style.id, { instructions: e.target.value })
                                  }
                                  placeholder={dictionary[INSTRUCTION_PLACEHOLDER_KEYS[configIndex % INSTRUCTION_PLACEHOLDER_KEYS.length]]?.[language as Language] || ""}
                                  className="min-h-[48px] resize-none border-border/60 text-xs"
                                  rows={2}
                                />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>

      {/* Fixed footer */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/60 shadow-[0_-4px_12px_rgba(0,0,0,0.03)] flex-shrink-0">
        <div className="flex items-center justify-center gap-1.5 mb-2.5 mx-auto w-fit px-3 py-1 rounded-lg bg-white border border-gray-200 shadow-sm">
          <span className="text-[11px] font-bold text-gray-800">
            {selectedStyles.length} {selectedStyles.length === 1 ? (dictionary["ad"]?.[language] || "Anuncio") : (dictionary["ads"]?.[language] || "Anuncios")}
          </span>
          <span className="text-[11px] text-gray-400">·</span>
          <span className="text-[11px] font-medium text-gray-500">
            {dictionary["cost"]?.[language] || "Costo"}:
          </span>
          <img src={CoinFluxi} alt="credits" className="w-4 h-4" style={{ filter: "brightness(0) saturate(100%) invert(67%) sepia(54%) saturate(1000%) hue-rotate(5deg) brightness(98%) contrast(89%)" }} />
          <span className="text-[11px] font-bold text-fluxi">
            {totalCredits} {dictionary["credits"]?.[language] || "créditos"}
          </span>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate}
          className={cn(
            "w-full flex items-center justify-center gap-2 h-10 text-sm font-bold rounded-xl",
            "text-white shadow-lg shadow-primary/20",
            "hover:shadow-xl hover:shadow-primary/25 hover:scale-[1.005]",
            "active:scale-[0.995] transition-all duration-150",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          )}
          style={{ background: "linear-gradient(to right, #4368E5, #4368E5)" }}
        >
          <Sparkles className="h-4 w-4" />
          {dictionary["generate-images"]?.[language] || "Generar Imágenes"}
        </button>
      </div>
    </div>
  );
}
